/**
 * FileDialog strict validators. Declares only FileDialog's own members, the ones
 * doc/classes/FileDialog.xml lists without `overrides=`. The overrides
 * (`dialog_hide_on_ok`, `size`, `title`) and every member from ConfirmationDialog up
 * arrive through the NODE_BASE_TYPES base walk.
 */
import '../confirmationdialog/linterParser.js';
import { FILE_DIALOG_ACCESS_NAMES, FILE_DIALOG_DISPLAY_MODE_NAMES, FILE_DIALOG_FILE_MODE_NAMES, } from '../../../godot/scene/fileDialog.js';
import { validatorRegistry } from '../../../linter/ValidatorRegistry.js';
import { propertyListFamilyEntry } from '../../../linter/validators/indexedFamily.js';
import { FILE_DIALOG_OPTIONS } from '../../../godot/classdb/propertyListFamilies.js';
import { v } from '../../../linter/validators/index.js';
/**
 * The three leaves `PropertyListHelper` emits per option (file_dialog.cpp:2201-2203).
 * `name` and `values` are format-only: `set_option_name` (:1977) and
 * `set_option_values` (:1989) assign once `ERR_FAIL_INDEX` passes the option index.
 */
const OPTION_LEAVES = {
    name: v.quotedString('name'),
    values: v.packedStringArray('values', '"*.png"'),
    // `set_option_default` (:2006-2015) clamps against the sibling `values` length
    // (file_dialog.cpp:2012-2014), which this validator cannot see. Both branches
    // floor at 0, so only the floor is checkable.
    default: v.int('default', {
        enforcedMin: { at: 0 },
        enforced: { min: 'file_dialog.cpp:2012' },
    }),
};
/**
 * Dispatches `option_<index>/<leaf>` to its leaf's validator. The negative index
 * bound lives here and the `>= option_count` bound in `linter.ts`, as in PopupMenu:
 * only the second needs a sibling, and a rule claiming both would double-report.
 */
const optionFamilyEntry = propertyListFamilyEntry(FILE_DIALOG_OPTIONS, {
    leaves: OPTION_LEAVES,
    unknownCode: 'INVALID_OPTION_KEY',
    describes: 'option name, values or default',
    negativeIndex: {
        cite: 'property_list_helper.cpp:58',
        code: 'INVALID_OPTION_INDEX',
        message: (index) => `Option index ${index} must be non-negative. FileDialog's property helper refuses a negative index (property_list_helper.cpp:58), so this property is never applied`,
    },
});
// `current_dir`, `current_file` and `current_path` are live UI state with PROPERTY_USAGE_NONE
// (file_dialog.cpp:2136-2138), which the storage guard (packed_scene.cpp:865) never saves, so no
// validator. `dialog_text` keeps AcceptDialog's validator: it is hidden but no removal
// (`linter/removalKeys.md`).
validatorRegistry.registerAll('FileDialog', optionFamilyEntry, {
    // file_dialog.cpp:2113, BOOL, no hint. set_mode_overrides_title (:1354-1356)
    // is a bare assignment.
    mode_overrides_title: v.boolean('mode_overrides_title'),
    file_mode: v.enumInt('file_mode', FILE_DIALOG_FILE_MODE_NAMES, { enforced: 'file_dialog.cpp:1363' }),
    display_mode: v.enumInt('display_mode', FILE_DIALOG_DISPLAY_MODE_NAMES, {
        enforced: 'file_dialog.cpp:1417',
    }),
    access: v.enumInt('access', FILE_DIALOG_ACCESS_NAMES, { enforced: 'file_dialog.cpp:1510' }),
    // file_dialog.cpp:2117, STRING, no hint. set_root_subfolder (:1332-1348) assigns
    // before its `dir_exists` guard, and the offline linter has no filesystem to
    // check that guard against.
    root_subfolder: v.quotedString('root_subfolder'),
    // Format-only: set_filters (:1259-1266) assigns the vector with no per-element check.
    // `can_convert_strict` lists ARRAY as a source for PACKED_STRING_ARRAY
    // (variant.cpp:467-473), so `filters = ["*.png"]` loads, measured on 4.6.3.
    filters: v.packedStringArray('filters', '"*.png"'),
    // file_dialog.cpp:2119, STRING, no hint. set_filename_filter (:1268-1276)
    // is a bare assignment.
    filename_filter: v.quotedString('filename_filter'),
    // file_dialog.cpp:2120, BOOL, no hint. set_show_hidden_files (:2213-2218)
    // is a bare assignment.
    show_hidden_files: v.boolean('show_hidden_files'),
    // file_dialog.cpp:2121, BOOL, no hint. set_use_native_dialog (:2261-2263)
    // assigns unconditionally before any native-dialog side effect.
    use_native_dialog: v.boolean('use_native_dialog'),
    // file_dialog.cpp:2123 (ADD_ARRAY_COUNT), INT, PROPERTY_HINT_NONE
    // (class_db.cpp:1492 passes no hint). set_option_count (:2038-2039) is
    // `ERR_FAIL_COND(p_count < 0)`: enforced floor.
    option_count: v.strictNonNegativeInt('option_count', { enforced: 'file_dialog.cpp:2039' }),
    // The "Customization" bools (file_dialog.cpp:2126-2134) share
    // `set_customization_flag_enabled` (file_dialog.cpp:1495-1502). Its ERR_FAIL_INDEX
    // (:1496) checks the flag that ADD_PROPERTYI bakes in at bind time, never the
    // stored bool, so none carries a bound.
    hidden_files_toggle_enabled: v.boolean('hidden_files_toggle_enabled'),
    file_filter_toggle_enabled: v.boolean('file_filter_toggle_enabled'),
    file_sort_options_enabled: v.boolean('file_sort_options_enabled'),
    folder_creation_enabled: v.boolean('folder_creation_enabled'),
    favorites_enabled: v.boolean('favorites_enabled'),
    recent_list_enabled: v.boolean('recent_list_enabled'),
    layout_toggle_enabled: v.boolean('layout_toggle_enabled'),
    overwrite_warning_enabled: v.boolean('overwrite_warning_enabled'),
    deleting_enabled: v.boolean('deleting_enabled'),
});
//# sourceMappingURL=linterParser.js.map